/**
 * Typed API errors for the Vello real clients (S097; M12). The server answers failures with RFC 7807
 * problem+json — `{ type, title, status, code, detail, correlationId }` (S080 `@vello/http-runtime`). This
 * module mirrors the server's public error codes and adds three transport-level codes so a screen can branch
 * on a stable, exhaustive union instead of parsing an HTTP status. The client never throws for an expected
 * API failure; it returns `err(ApiError)`.
 */
const STATUS_TO_CODE = {
    400: 'BAD_REQUEST',
    401: 'UNAUTHORIZED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    409: 'CONFLICT',
    429: 'RATE_LIMITED',
    500: 'INTERNAL',
    503: 'SERVICE_UNAVAILABLE',
};
const KNOWN_CODES = new Set([
    'BAD_REQUEST',
    'UNAUTHORIZED',
    'FORBIDDEN',
    'NOT_FOUND',
    'CONFLICT',
    'RATE_LIMITED',
    'SERVICE_UNAVAILABLE',
    'INTERNAL',
]);
function codeForStatus(status) {
    const mapped = STATUS_TO_CODE[status];
    if (mapped !== undefined)
        return mapped;
    if (status >= 500)
        return 'INTERNAL';
    return 'BAD_REQUEST';
}
function parseRetryAfter(headers) {
    const raw = headers['retry-after'];
    if (raw === undefined)
        return undefined;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
}
/**
 * Map a non-2xx HTTP response to an `ApiError`. The `code` prefers the problem document's own `code` (when it
 * is one we recognize) and falls back to the HTTP status, so an unexpected body never yields an out-of-union
 * code. `correlationId` prefers the response header, then the body — either way the app can quote it in a
 * support ticket. A body that is not valid problem+json still yields a well-formed error from the status.
 */
export function apiErrorFromResponse(status, headers, bodyText, keepMembers = []) {
    let body;
    try {
        const parsed = bodyText.length > 0 ? JSON.parse(bodyText) : undefined;
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            body = parsed;
        }
    }
    catch {
        body = undefined;
    }
    const bodyCode = typeof body?.code === 'string' && KNOWN_CODES.has(body.code) ? body.code : undefined;
    const code = bodyCode ?? codeForStatus(status);
    const detail = typeof body?.detail === 'string' ? body.detail : undefined;
    const correlationId = headers['x-correlation-id'] ?? (typeof body?.correlationId === 'string' ? body.correlationId : undefined);
    const retryAfterSeconds = parseRetryAfter(headers);
    // Only a well-formed fixed token is surfaced (the server emits the same shape), never free text.
    const reasonCode = typeof body?.reasonCode === 'string' && /^[A-Z0-9_]{1,64}$/.test(body.reasonCode) ? body.reasonCode : undefined;
    const params = paramsOf(body?.params);
    const kept = keepMembers.filter((k) => body !== undefined && Object.prototype.hasOwnProperty.call(body, k) && body[k] !== undefined);
    const members = kept.length > 0 ? Object.fromEntries(kept.map((k) => [k, body?.[k]])) : undefined;
    return {
        code,
        status,
        ...(detail !== undefined ? { detail } : {}),
        ...(correlationId !== undefined ? { correlationId } : {}),
        ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
        ...(reasonCode !== undefined ? { reasonCode } : {}),
        ...(params !== undefined ? { params } : {}),
        ...(members !== undefined ? { members } : {}),
    };
}
/** The problem's arguments: finite numbers and short fixed tokens only (the server emits the same shape). */
function paramsOf(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return undefined;
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
        if (!/^[a-z][A-Za-z0-9]{0,31}$/.test(k))
            continue;
        if ((typeof v === 'number' && Number.isFinite(v)) || (typeof v === 'string' && /^[A-Za-z0-9_.:-]{1,64}$/.test(v)))
            out[k] = v;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
/** A transport-level error (no usable response): a network drop, a deadline, or a body that would not parse. */
export function transportError(code, detail) {
    return { code, status: 0, ...(detail !== undefined ? { detail } : {}) };
}
//# sourceMappingURL=errors.js.map